// Cat companion — sprite-sheet driven cat that lounges in the dead
// space under the stats box during play.
//
// Art: Aichan's animated pixel cat (itch.io). Licensed for use,
// NOT for editing or redistribution — so the PNG ships untouched
// and recoloring happens at runtime via a canvas source-in tint
// that paints the silhouette in the active theme accent each frame.
// The original art on disk is never altered. (The hover heart is
// drawn programmatically, not sourced from the sheet.)
//
// State machine: idle in sit / stand / sleep; a 12-20s timer
// occasionally picks a new state and plays the matching one-shot
// transition into it. Hover swaps to the stand idle with a small
// bobbing heart above the cat's head, plus a continuous purr (sfx +
// a faint CSS vibration).
//
// Alignment contract (user-specified): the cat's FEET sit exactly on
// the playfield's bottom line for every animation (per-anim baseline
// measured from the sheet), and its back foot anchors at the stats
// box's bottom corner. The container is just an anchor box — the
// canvas is absolutely positioned off its bottom-right and may
// legally overhang below (tail) per the contract.

import type { HowlerAudioEngine } from '../audio-howler/audio';

const SHEET_URL = '/cat-animations.png';
const CELL_W = 33;
const CELL_H = 32;

/** Sprite x (px) of the cat's back foot. The cat faces RIGHT (head on
 *  the high-x side, tail curling on the left), so the rear paw sits
 *  near the sprite's left edge — anchored to the container's LEFT
 *  edge so the back foot lands on the stats box's bottom-left corner.
 *  The tail may swing past it. */
const BACK_FOOT_X = 5;

interface AnimDef {
  /** Sheet row (0-indexed top to bottom). */
  row: number;
  /** Frame counts MEASURED from the sheet's alpha (don't trust the
   *  poster) — eat is 4, not 5; sit_idle 4; sleep_idle 5. A wrong
   *  count here plays an empty cell as a blank frame. */
  frames: number;
  fps: number;
  loop: boolean;
  /** Sprite y of the feet line (lowest row with >= 3 opaque px on the
   *  anim's settled frame). Drives the bottom offset so feet stay on
   *  the playfield line across animations. */
  baseline: number;
}

const ANIM: Record<string, AnimDef> = {
  stand_to_sit:   { row: 0, frames: 4, fps: 7, loop: false, baseline: 28 },
  sit_idle:       { row: 1, frames: 4, fps: 3, loop: true,  baseline: 28 },
  sit_to_stand:   { row: 2, frames: 4, fps: 7, loop: false, baseline: 26 },
  stand_to_sleep: { row: 3, frames: 5, fps: 5, loop: false, baseline: 28 },
  sleep_idle:     { row: 4, frames: 5, fps: 2, loop: true,  baseline: 27 },
  sleep_to_stand: { row: 5, frames: 5, fps: 5, loop: false, baseline: 25 },
  stand_idle:     { row: 6, frames: 5, fps: 4, loop: true,  baseline: 25 },
  eat:            { row: 7, frames: 4, fps: 5, loop: true,  baseline: 25 },
  walk:           { row: 8, frames: 8, fps: 8, loop: true,  baseline: 25 },
};

/** 5x5 pixel heart drawn above the head while petting. Programmatic —
 *  not sliced from the sheet. */
const HEART: string[] = [
  '01010',
  '11111',
  '11111',
  '01110',
  '00100',
];

type CatState = 'sit' | 'stand' | 'sleep';

/** State transitions — direct routes only. Sit↔sleep has no direct
 *  animation in the asset; the state machine routes through stand. */
const DIRECT_TRANSITION: Partial<Record<`${CatState}-${CatState}`, keyof typeof ANIM>> = {
  'stand-sit':    'stand_to_sit',
  'sit-stand':    'sit_to_stand',
  'stand-sleep':  'stand_to_sleep',
  'sleep-stand':  'sleep_to_stand',
};

export class CatCompanion {
  readonly root: HTMLElement;
  private canvas: HTMLCanvasElement;
  private ctx: CanvasRenderingContext2D;
  private sheet: HTMLImageElement;
  private sheetReady = false;

  private current: AnimDef = ANIM.sit_idle!;
  private frame = 0;
  private accMs = 0;
  private state: CatState = 'sit';
  /** Queued state to settle into once any active transition finishes. */
  private targetState: CatState = 'sit';

  private hovering = false;
  private idleTimer: number | null = null;
  private rafId: number | null = null;
  private lastTime = 0;
  private tintCache = '#ff3da6';
  /** Container zone height — drives the display scale. */
  private zoneH = 0;

  constructor(private audio: HowlerAudioEngine) {
    this.root = document.createElement('div');
    this.root.id = 'playfield-cat';
    this.root.style.display = 'none';
    this.canvas = document.createElement('canvas');
    // Native resolution: one cell. CSS upscales with image-rendering:
    // pixelated for crisp tetris-style pixel art.
    this.canvas.width = CELL_W;
    this.canvas.height = CELL_H;
    this.root.appendChild(this.canvas);
    this.ctx = this.canvas.getContext('2d', { willReadFrequently: false })!;
    this.ctx.imageSmoothingEnabled = false;
    this.sheet = new Image();
    this.sheet.onload = () => { this.sheetReady = true; };
    this.sheet.src = SHEET_URL;

    // Hover targets the CANVAS (the cat itself), not the whole dead
    // zone — petting should require actually touching the cat.
    this.canvas.addEventListener('mouseenter', () => this.onHover(true));
    this.canvas.addEventListener('mouseleave', () => this.onHover(false));
    this.scheduleIdleChange();
  }

  // ─── Animation control ────────────────────────────────────────────

  private setAnim(name: keyof typeof ANIM): void {
    this.current = ANIM[name]!;
    this.frame = 0;
    this.accMs = 0;
    this.applyLayout();
  }

  private idleAnimFor(s: CatState): keyof typeof ANIM {
    return s === 'sit' ? 'sit_idle' : s === 'stand' ? 'stand_idle' : 'sleep_idle';
  }

  /** Pick a new target state and start the appropriate transition.
   *  Two-step (sit↔sleep) routes through stand automatically once the
   *  intermediate transition lands and the next idle change fires. */
  private nudgeState(): void {
    if (this.hovering) return;
    // Weighted random: sit is the calm baseline, sleep is a treat.
    const r = Math.random();
    const next: CatState = r < 0.55 ? 'sit' : r < 0.85 ? 'stand' : 'sleep';
    if (next === this.state) return;
    const direct = DIRECT_TRANSITION[`${this.state}-${next}` as `${CatState}-${CatState}`];
    if (direct) {
      this.targetState = next;
      this.setAnim(direct);
    } else {
      // sit↔sleep: route through stand first. The next nudgeState pass
      // (in ~12-20s) will pick the final destination on its own.
      this.targetState = 'stand';
      const via = DIRECT_TRANSITION[`${this.state}-stand` as `${CatState}-${CatState}`];
      if (via) this.setAnim(via);
    }
  }

  private scheduleIdleChange(): void {
    this.idleTimer = window.setTimeout(() => {
      if (this.root.style.display !== 'none' && !this.hovering) this.nudgeState();
      this.scheduleIdleChange();
    }, 12000 + Math.random() * 8000);
  }

  // ─── Hover (stand + heart + purr) ─────────────────────────────────

  private onHover(active: boolean): void {
    if (active === this.hovering) return;
    this.hovering = active;
    if (active) {
      this.root.classList.add('purring');
      // Continuous purr voice — a sustained ~23Hz pulse, started and
      // stopped with the hover instead of retriggered one-shots
      // (which read as 'stabs' rather than a real purr).
      this.audio.startPurr();
      this.setAnim('stand_idle');
    } else {
      this.root.classList.remove('purring');
      this.audio.stopPurr();
      this.setAnim(this.idleAnimFor(this.state));
    }
  }

  // ─── Frame loop ───────────────────────────────────────────────────

  private update(now: number): void {
    if (this.lastTime === 0) this.lastTime = now;
    const dt = Math.min(80, now - this.lastTime);
    this.lastTime = now;
    this.accMs += dt;
    const frameMs = 1000 / this.current.fps;
    while (this.accMs >= frameMs) {
      this.accMs -= frameMs;
      this.frame++;
      if (this.frame >= this.current.frames) {
        if (this.current.loop) {
          this.frame = 0;
        } else {
          // Transition completed → commit the target state and start
          // its idle. If the user hovered mid-transition, hover wins.
          this.frame = this.current.frames - 1;
          if (!this.hovering) {
            this.state = this.targetState;
            this.setAnim(this.idleAnimFor(this.state));
          }
          break;
        }
      }
    }
    this.draw(now);
    this.rafId = requestAnimationFrame((t) => this.update(t));
  }

  private draw(now: number): void {
    if (!this.sheetReady) return;
    const c = this.ctx;
    c.clearRect(0, 0, CELL_W, CELL_H);
    // Pull the active accent off CSS so palette swaps propagate live.
    const tint = (getComputedStyle(this.root).color || this.tintCache).trim() || this.tintCache;
    this.tintCache = tint;
    const sx = this.frame * CELL_W;
    const sy = this.current.row * CELL_H;
    // Draw the original sprite, then recolor by composite — the
    // ORIGINAL PNG on disk is never modified (the asset license
    // prohibits editing the source file).
    c.globalCompositeOperation = 'source-over';
    c.drawImage(this.sheet, sx, sy, CELL_W, CELL_H, 0, 0, CELL_W, CELL_H);
    // Petting: a small heart bobs above the head (the cat faces
    // RIGHT — head on the high-x side). Drawn BEFORE the tint pass so
    // the heart receives exactly the same theme treatment as the
    // cat's body; its pulsing alpha survives the source-in fill.
    if (this.hovering) {
      const t = now / 1000;
      const bob = Math.round(Math.sin(t * 2.6) * 1.5);
      c.globalAlpha = 0.72 + 0.28 * Math.sin(t * 3.4);
      c.fillStyle = '#ffffff';
      const hx = 21;
      const hy = 1 + bob;
      for (let y = 0; y < HEART.length; y++) {
        const rowBits = HEART[y]!;
        for (let x = 0; x < rowBits.length; x++) {
          if (rowBits[x] === '1') c.fillRect(hx + x, hy + y, 1, 1);
        }
      }
      c.globalAlpha = 1;
    }
    c.globalCompositeOperation = 'source-in';
    c.fillStyle = tint;
    c.fillRect(0, 0, CELL_W, CELL_H);
    c.globalCompositeOperation = 'source-over';
  }

  // ─── Layout ───────────────────────────────────────────────────────

  /** Compute the canvas's display size + the bottom/right offsets that
   *  pin the current animation's feet to the container's bottom edge
   *  (the playfield's bottom line) and the back foot to the right
   *  edge (the stats box's corner). Re-run on zone resize and on
   *  every animation change (baselines differ per pose). */
  private applyLayout(): void {
    if (this.zoneH <= 0) return;
    const dispH = Math.max(110, Math.min(190, this.zoneH * 1.25));
    const s = dispH / CELL_H;
    const st = this.canvas.style;
    st.height = `${Math.round(dispH)}px`;
    st.width = `${Math.round(CELL_W * s)}px`;
    // Feet (bottom edge of the baseline row) land exactly on the
    // container's bottom; everything below the baseline (tail frames)
    // may overhang the line.
    st.bottom = `${-Math.round((CELL_H - 1 - this.current.baseline) * s)}px`;
    // Back foot at the container's LEFT edge (the stats box's
    // bottom-left corner); the body extends rightward under the box.
    st.left = `${-Math.round(BACK_FOOT_X * s)}px`;
    st.right = 'auto';
  }

  // ─── Lifecycle ────────────────────────────────────────────────────

  show(): void {
    if (this.root.style.display !== 'none') return;
    this.root.style.display = '';
    if (this.rafId == null) {
      this.lastTime = 0;
      this.rafId = requestAnimationFrame((t) => this.update(t));
    }
  }

  hide(): void {
    if (this.root.style.display === 'none') return;
    this.root.style.display = 'none';
    this.root.classList.remove('purring');
    this.audio.stopPurr();
    if (this.rafId != null) { cancelAnimationFrame(this.rafId); this.rafId = null; }
    this.hovering = false;
  }

  /** Anchor box for the dead zone under the stats box: bottom edge is
   *  the playfield's bottom line, right edge is the stats box corner.
   *  Hides when the layout leaves no room (small windows / versus). */
  position(left: number, top: number, width: number, height: number): void {
    if (height < 76 || width < 90) {
      this.root.style.visibility = 'hidden';
      return;
    }
    this.root.style.visibility = '';
    this.root.style.left = `${Math.round(left)}px`;
    this.root.style.top = `${Math.round(top)}px`;
    this.root.style.width = `${Math.round(width)}px`;
    const h = Math.round(height);
    this.root.style.height = `${h}px`;
    if (h !== this.zoneH) {
      this.zoneH = h;
      this.applyLayout();
    }
  }
}
